// ==================== VENDOR PAYMENTS SERVICE ====================
// Extracted from database.js - contains all vendor payments (accounts payable) functionality

import { 
  collection, 
  doc, 
  getDocs, 
  getDoc,
  addDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  orderBy, 
  serverTimestamp 
} from 'firebase/firestore';
import { db } from '../../utils/database/firebase-config';
// NOTE: Service dependencies commented out to avoid circular imports during refactoring
// import { billsService } from './bills';
// import { expensesService } from '../accounting/expenses';
// import { chartOfAccountsService } from '../accounting/chart-of-accounts';
// import { journalEntriesService } from '../accounting/journal-entries';

export const vendorPaymentsService = {
  async getVendorPayments(companyId) {
    try {
      const paymentsRef = collection(db, 'vendorPayments');
      const q = query(paymentsRef, 
        where('companyId', '==', companyId),
        orderBy('paymentDate', 'desc')
      );
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
      console.warn('Index error, trying without ordering:', error.message);
      // Fallback: get payments without ordering if index doesn't exist
      try {
        const paymentsRef = collection(db, 'vendorPayments');
        const q = query(paymentsRef, 
          where('companyId', '==', companyId)
        );
        const snapshot = await getDocs(q);
        const payments = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        // Sort in JavaScript as fallback
        return payments.sort((a, b) => {
          const dateA = a.paymentDate?.toDate?.() || new Date(a.paymentDate || 0);
          const dateB = b.paymentDate?.toDate?.() || new Date(b.paymentDate || 0);
          return dateB - dateA;
        });
      } catch (fallbackError) {
        console.error('Error loading vendor payments:', fallbackError);
        return [];
      }
    }
  },
  
  async addVendorPayment(paymentData) {
    try {
      // Create vendor payment record
      const paymentsRef = collection(db, 'vendorPayments');
      const docRef = await addDoc(paymentsRef, {
        ...paymentData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        status: 'completed'
      });
      
      // Update bill payment status if bill is provided
      if (paymentData.billId) {
        const billRef = doc(db, 'bills', paymentData.billId);
        const billSnap = await getDoc(billRef);
        
        if (billSnap.exists()) {
          const billData = billSnap.data();
          const currentPaidAmount = billData.paidAmount || 0;
          const newPaidAmount = currentPaidAmount + paymentData.amount;
          const totalAmount = billData.totalAmount || 0;
          
          // Determine payment status
          let paymentStatus;
          if (newPaidAmount >= totalAmount) {
            paymentStatus = 'paid';
          } else if (newPaidAmount > 0) {
            paymentStatus = 'partial';
          } else {
            paymentStatus = 'unpaid';
          }
          
          // Update bill payment status
          await updateDoc(billRef, {
            paidAmount: newPaidAmount,
            paymentStatus: paymentStatus,
            status: paymentStatus === 'paid' ? 'paid' : 'pending',
            updatedAt: serverTimestamp()
          });
          
          // Update corresponding expense status
          try {
            const expensesRef = collection(db, 'expenses');
            const expenseQuery = query(expensesRef, 
              where('companyId', '==', paymentData.companyId),
              where('billId', '==', paymentData.billId)
            );
            const expenseSnapshot = await getDocs(expenseQuery);
            
            if (!expenseSnapshot.empty) {
              const expenseDoc = expenseSnapshot.docs[0];
              const isFullyPaid = newPaidAmount >= totalAmount;
              
              await updateDoc(expenseDoc.ref, {
                status: isFullyPaid ? 'paid' : 'partially_paid',
                paidAmount: newPaidAmount,
                paymentStatus: isFullyPaid ? 'paid' : 'partial',
                updatedAt: serverTimestamp()
              });
            }
          } catch (expenseError) {
            console.error('Error updating expense status:', expenseError);
            // Don't fail the payment if expense update fails
          }
        }
      }
      
      // Update vendor balance
      try {
        await this.updateVendorBalance(paymentData.vendorId, paymentData.companyId);
      } catch (balanceError) {
        console.error('Error updating vendor balance:', balanceError);
        // Don't fail the payment if balance update fails
      }
      
      // Create journal entry for payment (with improved error handling)
      try {
        // TODO: Reconnect with journalEntriesService after refactoring
        // await this.createVendorPaymentJournalEntry(
        //   paymentData.companyId,
        //   { ...paymentData, id: docRef.id },
        //   paymentData.createdBy
        // );
      } catch (journalError) {
        console.error('Error creating journal entry:', journalError);
        // Don't fail the payment if journal entry fails
      }
      
      return docRef.id;
      
    } catch (error) {
      throw error;
    }
  },
  
  async updateVendorPayment(paymentId, paymentData) {
    const paymentRef = doc(db, 'vendorPayments', paymentId);
    await updateDoc(paymentRef, { ...paymentData, updatedAt: serverTimestamp() });
    return true;
  },
  
  async deleteVendorPayment(paymentId) {
    const paymentRef = doc(db, 'vendorPayments', paymentId);
    await deleteDoc(paymentRef);
    return true;
  },
  
  async updateVendorBalance(vendorId, companyId) {
    try {
      // Get all bills for this vendor
      const billsRef = collection(db, 'bills');
      const billsQuery = query(billsRef, 
        where('vendorId', '==', vendorId),
        where('companyId', '==', companyId)
      );
      const billsSnapshot = await getDocs(billsQuery);
      const bills = billsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      
      // Get all payments for this vendor
      const paymentsRef = collection(db, 'vendorPayments');
      const paymentsQuery = query(paymentsRef, 
        where('vendorId', '==', vendorId),
        where('companyId', '==', companyId)
      );
      const paymentsSnapshot = await getDocs(paymentsQuery);
      const payments = paymentsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      
      // Calculate totals
      const totalBilled = bills.reduce((sum, bill) => sum + (bill.totalAmount || 0), 0);
      const totalPaid = payments.reduce((sum, payment) => sum + (payment.amount || 0), 0);
      const outstanding = totalBilled - totalPaid;
      
      // Update vendor record
      const vendorRef = doc(db, 'vendors', vendorId);
      await updateDoc(vendorRef, {
        balance: outstanding,
        outstanding: outstanding,
        totalBilled: totalBilled,
        totalPaid: totalPaid,
        totalBills: bills.length,
        totalPayments: payments.length,
        updatedAt: serverTimestamp()
      });
      
      return true;
    } catch (error) {
      console.error('Error updating vendor balance:', error);
      throw error;
    }
  },

  async createVendorPaymentJournalEntry(companyId, payment, createdBy) {
    // TODO: Reconnect with chartOfAccountsService and journalEntriesService after refactoring
    // const chartOfAccounts = await chartOfAccountsService.getChartOfAccounts(companyId);
    // const accountsPayableAccount = chartOfAccounts.find(acc => acc.code === '2010' || acc.name.includes('Accounts Payable'));
    // const cashAccount = chartOfAccounts.find(acc => acc.code === '1010' || acc.name.includes('Cash'));
    // if (!accountsPayableAccount || !cashAccount) throw new Error('Required accounts not found for vendor payment journal entry');
    // const journalEntryData = { ... };
    // const journalEntryId = await journalEntriesService.addJournalEntry(journalEntryData);
    // await this.updateVendorPayment(payment.id, { journalEntryId });
    // return journalEntryId;
    return null; // Placeholder
  }
}; 